import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Clock, BookOpen, Award, Users, ShieldCheck,
  Sparkles, GraduationCap, FileBadge2, ChevronDown, Lock, CreditCard,
  Wallet, Building2,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StickyPriceBar from "../components/StickyPriceBar";
import SEO from "../components/SEO";
import PresentazioneVideo from "../components/PresentazioneVideo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { useToast } from "../hooks/use-toast";
import useReveal from "../hooks/useReveal";
import { COURSE, PAYMENT_METHODS } from "../data/mock";
import { PROMO, prezzoListinoPacchetto } from "../data/promo";
import CertificazioniModal, { PACCHETTI_CORSO } from "../components/CertificazioniModal";
import CheckoutModal from "../components/CheckoutModal";

function formatPrice(v) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(v);
}

const COURSE_OPTIONS = [
  {
    id: "only-course",
    title: "Solo Corso",
    badge: "Solo soci",
    soloSoci: true,
    price: 99,
    items: [
      { label: "Corso A.C.D.T. per la qualificazione professionale (20 ore)", price: 99 },
      { label: "Consulenza Tutela Digitale base", price: 0, free: true },
    ],
  },
  {
    id: "course-tessera",
    title: "Corso + Tessera ACDT",
    badge: "Consigliato",
    price: 149,
    items: [
      { label: "Corso A.C.D.T. per la qualificazione professionale (20 ore)", price: 99 },
      { label: "Tessera Associativa ACDT", price: 50 },
      { label: "Consulenza Tutela Digitale base", price: 0, free: true },
    ],
  },
  {
    id: "premium",
    title: "Corso + Tessera + Registro Agg. MIMIT",
    badge: "Professionista",
    price: 218.9,
    items: [
      { label: "Corso A.C.D.T. per la qualificazione professionale (20 ore)", price: 99 },
      { label: "Tessera Associativa ACDT", price: 50 },
      { label: "Iscrizione Registro Nazionale Agg. MIMIT", price: 69.9 },
      { label: "Consulenza Tutela Digitale base", price: 0, free: true },
    ],
  },
];

export default function CoursePage() {
  useReveal();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Corso Consulente Digitale Tecnologico e di Sostenibilità – A.C.D.T.";
  }, []);

  return (
    <div className="relative overflow-x-hidden">
      <SEO />
      <Navbar />
      <CourseHero />
      <PresentazioneUfficiale />
      <WhyNeeded />
      <Modules />
      <Topics />
      <Audience />
      <PurchaseSection />
      <FAQ />
      <Footer />
      <StickyPriceBar />
    </div>
  );
}

function CourseHero() {
  return (
    <section className="relative pt-12 md:pt-16 pb-16 md:pb-24 overflow-hidden">
      <div className="absolute inset-0 grid-bg"></div>
      <div className="orb bg-[#102a6b]" style={{ width: 480, height: 480, top: -160, left: -100 }}></div>
      <div className="orb bg-[#f59e0b]" style={{ width: 420, height: 420, top: 80, right: -120, opacity: 0.35 }}></div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="reveal">
          <Link to="/" className="inline-flex items-center gap-2 text-[12px] font-semibold text-[#102a6b] hover:underline">
            <span className="opacity-60">A.C.D.T.</span>
            <span className="opacity-30">/</span>
            <span>Corso</span>
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 backdrop-blur border border-slate-200 text-[12px] font-semibold text-[#102a6b] shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-[#f59e0b]" />
            Corso A.C.D.T. per la qualificazione professionale del Consulente Digitale, Tecnologico e di Sostenibilità
          </div>
          <h1 className="mt-5 text-4xl md:text-5xl lg:text-[54px] leading-[1.05] font-extrabold text-[#0a1f55]">
            {COURSE.title}
          </h1>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed max-w-xl">
            {COURSE.description}
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <div className="flex items-baseline gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 shadow-sm" data-testid="hero-price-box">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 mr-1">Da</span>
              {PROMO.active && (
                <span className="text-lg font-bold text-slate-400 line-through">{formatPrice(PROMO.prezzoListinoCorso)}</span>
              )}
              <span className="text-3xl font-extrabold text-[#102a6b]">{formatPrice(COURSE.price)}</span>
              {PROMO.active && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-extrabold uppercase tracking-wider">-{PROMO.scontoEuro}€</span>
              )}
            </div>
            <a href="#acquista">
              <Button size="lg" className="bg-[#102a6b] hover:bg-[#0a1f55] text-white h-12 px-6 shadow-lg shadow-[#102a6b]/20">
                Acquista il Corso
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            {PROMO.active && (
              <div className="inline-flex items-center gap-1.5 text-[12px] font-bold text-red-600 uppercase tracking-wider">
                <Sparkles className="h-3.5 w-3.5" /> {PROMO.labelOfferta}
              </div>
            )}
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Meta icon={Clock} k="Durata" v="20 ore" />
            <Meta icon={BookOpen} k="Moduli" v="20" />
            <Meta icon={Award} k="Attestato" v="Incluso" />
            <Meta icon={ShieldCheck} k="Legge" v="4/2013" />
          </div>
        </div>

        <div className="relative reveal">
          <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_30px_80px_-30px_rgba(16,42,107,0.35)] aspect-[4/3] bg-slate-100">
            <img
              src={COURSE.hero}
              alt={COURSE.title}
              className="absolute inset-0 h-full w-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f55]/30 via-transparent to-transparent"></div>
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
              <div className="px-3 py-1.5 rounded-lg bg-white/90 backdrop-blur border border-white/60 text-[11px] font-bold text-[#0a1f55] uppercase tracking-wider">
                Figura Professionale Certificata
              </div>
              <div className="px-3 py-1.5 rounded-lg bg-[#f59e0b] text-[#0a1f55] text-[11px] font-extrabold uppercase tracking-wider">
                L. 4/2013
              </div>
            </div>
          </div>

          <div className="absolute -bottom-6 -left-4 hidden md:block float-y bg-white rounded-2xl border border-slate-200 shadow-xl p-4 w-56">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#102a6b] text-white flex items-center justify-center">
                <GraduationCap className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Riconoscimento</div>
                <div className="text-sm font-bold text-[#0a1f55]">Agg. MIMIT</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 mt-14">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {COURSE.meta.map((m) => (
            <div key={m.k} className="flex flex-col">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{m.k}</div>
              <div className="mt-1 text-[15px] font-bold text-[#0a1f55]">{m.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Meta({ icon: Icon, k, v }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="h-9 w-9 rounded-lg bg-[#102a6b]/10 text-[#102a6b] flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{k}</div>
        <div className="text-sm font-bold text-[#0a1f55]">{v}</div>
      </div>
    </div>
  );
}

function WhyNeeded() {
  return (
    <section className="relative py-20 md:py-24 bg-gradient-to-b from-white via-[#f5f7ff] to-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12">
        <div className="reveal">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">Perché</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a1f55] leading-[1.1]">
            Perché oggi serve una qualificazione professionale
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Il digitale non è più un territorio improvvisato. È un ambiente professionale
            complesso, con responsabilità, normative e impatti reali su persone e aziende.
            Chi opera senza una formazione strutturata e senza inquadramento corre rischi concreti:
          </p>
          <ul className="mt-5 space-y-2.5">
            {COURSE.whyNeeded.map((w) => (
              <li key={w} className="flex items-start gap-2.5 text-[14.5px] text-slate-700">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#f59e0b]"></span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-[15px] font-bold text-[#0a1f55]">
            Nel digitale non basta "saper fare". Serve essere qualificati, riconosciuti e tutelati.
          </p>
        </div>

        <div className="reveal rounded-3xl bg-[#0a1f55] text-white p-8 md:p-10 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#f59e0b]/20 blur-3xl"></div>
          <div className="relative">
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">La soluzione</div>
            <h3 className="mt-3 text-2xl md:text-3xl font-extrabold leading-tight">
              Un percorso strutturato e riconosciuto
            </h3>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Il Corso A.C.D.T. per la qualificazione professionale del Consulente Digitale, Tecnologico e di
              Sostenibilità nasce per rispondere a una necessità reale del mercato. È progettato per:
            </p>
            <ul className="mt-5 grid sm:grid-cols-2 gap-3">
              {COURSE.solutions.map((s) => (
                <li key={s} className="flex items-start gap-2 rounded-xl bg-white/[0.06] border border-white/10 p-3">
                  <Check className="h-4 w-4 mt-0.5 text-[#f59e0b] shrink-0" />
                  <span className="text-[13.5px] text-slate-200">{s}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-[15px] font-bold text-white">
              Non è un corso informativo. È un percorso di qualificazione professionale.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Modules() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 reveal">
          <div>
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">Programma</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a1f55] leading-[1.1]">
              20 moduli – 20 ore di formazione strutturata
            </h2>
          </div>
          <p className="max-w-md text-slate-600">
            Un percorso completo che copre normativa, tecnologia, etica e operatività
            professionale.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {COURSE.modules.map((m, i) => (
            <div
              key={m.n}
              className="reveal group relative rounded-2xl border border-slate-200 bg-white p-5 lift overflow-hidden"
              style={{ transitionDelay: `${(i % 12) * 35}ms` }}
            >
              <div className="absolute top-3 right-4 text-5xl font-extrabold text-[#102a6b]/[0.06] leading-none">
                {String(m.n).padStart(2, "0")}
              </div>
              <div className="relative">
                <div className="text-[10.5px] font-bold tracking-[0.2em] uppercase text-[#f59e0b]">
                  Modulo {m.n}
                </div>
                <h3 className="mt-2 text-[15px] font-bold text-[#0a1f55] leading-snug">
                  {m.title}
                </h3>
                <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{m.text}</p>
                <div className="mt-4 h-[3px] w-8 rounded-full bg-gradient-to-r from-[#102a6b] to-[#f59e0b] group-hover:w-16 transition-all duration-500"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PresentazioneUfficiale() {
  return (
    <section
      id="video-presentazione"
      className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a]"
      data-testid="course-video-presentazione"
    >
      {/* effetti luminosi decorativi */}
      <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#f59e0b]/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#fbbf24]/15 blur-3xl pointer-events-none" />
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
           style={{
             backgroundImage:
               "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
             backgroundSize: "36px 36px",
           }}
      />

      <div className="relative max-w-6xl mx-auto px-5 md:px-8">
        <div className="text-center reveal">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#f59e0b]/20 border border-[#fbbf24]/40 text-[#fbbf24] text-[11px] font-extrabold uppercase tracking-[0.24em] backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-[#fbbf24] animate-pulse" />
            Video di presentazione ufficiale
          </div>
          <h2 className="mt-5 text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-[1.05]">
            Guarda perché{" "}
            <span className="bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] bg-clip-text text-transparent">
              A.C.D.T.
            </span>{" "}
            è la scelta giusta
          </h2>
          <p className="mt-5 text-slate-200 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            In meno di due minuti scopri la nostra missione, il valore del percorso di
            qualificazione professionale e le opportunità per Consulenti Digitali.
          </p>
        </div>

        {/* Video XL con cornice glow */}
        <div className="mt-12 md:mt-14 reveal">
          <div className="relative group">
            {/* glow ring animato */}
            <div className="absolute -inset-0.5 rounded-[28px] bg-gradient-to-r from-[#f59e0b] via-[#fbbf24] to-[#f59e0b] opacity-70 blur-xl group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.7)]">
              <PresentazioneVideo rounded="rounded-3xl" />
            </div>
          </div>

          {/* Badges sotto video */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 md:gap-4 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/15 text-white text-xs md:text-sm font-semibold">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              Slide ufficiali A.C.D.T.
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/15 text-white text-xs md:text-sm font-semibold">
              Durata ~90/120 sec
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#f59e0b] text-[#0a1f55] text-xs md:text-sm font-extrabold uppercase tracking-wider">
              Prodotto dal Direttivo
            </div>
          </div>

          {/* CTA sotto video */}
          <div className="mt-10 md:mt-12 text-center reveal">
            <a
              href="#acquista"
              data-testid="course-video-cta-acquista"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] text-base font-extrabold shadow-2xl hover:shadow-[0_20px_60px_-15px_rgba(245,158,11,0.6)] hover:-translate-y-0.5 transition-all"
            >
              Iscriviti ora al corso
              <span aria-hidden>→</span>
            </a>
            <p className="mt-3 text-slate-300 text-xs">
              Certificazione L. 4/2013 · Attestato incluso · Sconto immediato
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Topics() {
  return (
    <section className="relative py-20 md:py-24 bg-gradient-to-b from-white via-[#f5f7ff] to-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 reveal">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">Contenuti del corso</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a1f55] leading-[1.1]">
            Il percorso affronta in modo strutturato
          </h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="aspect-[4/3] bg-slate-100 relative">
              <img src={COURSE.image2} alt="Corso" className="absolute inset-0 h-full w-full object-cover" onError={(e)=>{e.currentTarget.style.display="none";}} />
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 reveal">
          <ol className="space-y-3">
            {COURSE.topics.map((t, i) => (
              <li key={t} className="flex items-start gap-4 rounded-xl bg-white border border-slate-200 p-5 lift">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-[#102a6b] to-[#1e40af] text-white font-extrabold flex items-center justify-center shrink-0">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-[15px] font-bold text-[#0a1f55]">{t}</h3>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {COURSE.includes.map((it) => (
              <div key={it} className="flex items-center gap-2 text-[13.5px] text-slate-700">
                <Check className="h-4 w-4 text-[#102a6b]" />
                <span>{it}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Audience() {
  return (
    <section className="relative py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="reveal max-w-2xl">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">A chi si rivolge</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a1f55] leading-[1.1]">
            Questo percorso è pensato per
          </h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {COURSE.audience.map((a, i) => (
            <div
              key={a}
              className="reveal rounded-2xl border border-slate-200 bg-white p-6 lift flex items-center gap-4"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="h-12 w-12 rounded-xl bg-[#102a6b]/10 text-[#102a6b] flex items-center justify-center">
                <Users className="h-5 w-5" />
              </div>
              <h3 className="text-[15.5px] font-bold text-[#0a1f55]">{a}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PurchaseSection() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [optionId, setOptionId] = useState("course-tessera");
  const [checkoutOpt, setCheckoutOpt] = useState(null); // opzione in checkout (apre modal)
  const [submitting, setSubmitting] = useState(false);
  const [isSocio, setIsSocio] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [redirectNotice, setRedirectNotice] = useState(false);
  const [certModal, setCertModal] = useState(null);
  const { toast } = useToast();

  // Mappa optionId frontend → package_id backend
  const PKG_BACKEND = {
    "only-course": "only-course",
    "course-tessera": "tessera-corso",
    "premium": "premium",
  };

  // Verifica se l'utente è già socio (loggato + ha tessera)
  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then((r) => {
        const u = r.data || {};
        setIsSocio(Boolean(u.numero_tessera));
      })
      .catch(() => setIsSocio(false))
      .finally(() => setAuthChecked(true));
  }, [API]);

  const option = useMemo(
    () => COURSE_OPTIONS.find((o) => o.id === optionId) || COURSE_OPTIONS[0],
    [optionId]
  );

  const handleSelectOption = (o) => {
    if (o.soloSoci && !isSocio) {
      setOptionId("course-tessera");
      setRedirectNotice(true);
      toast({
        title: "Pacchetto riservato ai soci",
        description: "Per acquistare solo il corso devi essere tesserato. Ti abbiamo selezionato \"Corso + Tessera\".",
      });
      setTimeout(() => setRedirectNotice(false), 6000);
      // Apri modal comunque per il pacchetto di default
      setCheckoutOpt(COURSE_OPTIONS.find((x) => x.id === "course-tessera"));
      return;
    }
    setRedirectNotice(false);
    setOptionId(o.id);
    setCheckoutOpt(o);
  };

  const handleModalSubmit = async (billing) => {
    const package_id = PKG_BACKEND[checkoutOpt.id];
    if (!package_id) {
      toast({ title: "Pacchetto non disponibile online", description: "Contatta la segreteria." });
      return;
    }
    if (PACCHETTI_CORSO.includes(package_id)) {
      setCertModal({ billing, package_id });
      return;
    }
    await doCheckoutCourse({ package_id, billing, cert_l4: false, cert_cfu: false });
  };

  const doCheckoutCourse = async ({ package_id, billing, cert_l4, cert_cfu }) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/checkout/create-session`, {
        package_id,
        billing,
        origin_url: window.location.origin,
        certificazione_l4: !!cert_l4,
        certificazione_cfu: !!cert_cfu,
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      setSubmitting(false);
      toast({
        title: "Errore avvio pagamento",
        description: err.response?.data?.detail || err.message || "Riprova",
      });
    }
  };

  return (
    <section id="acquista" className="relative py-24 md:py-28 bg-gradient-to-b from-[#f5f7ff] to-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto reveal">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">Acquista</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-extrabold text-[#0a1f55] leading-[1.05]">
            Iscriviti al Corso
          </h2>
          <p className="mt-4 text-slate-600">
            Scegli la formula più adatta a te. Pagamento sicuro con <strong>carta</strong>, <strong>PayPal</strong> o <strong>Klarna</strong> (paga in 3 rate senza interessi).
          </p>

          {/* Membership required notice */}
          <div className="mt-6 inline-flex items-start gap-3 rounded-xl bg-[#f59e0b]/10 border border-[#f59e0b]/40 px-4 py-3 text-left max-w-2xl mx-auto">
            <div className="h-7 w-7 rounded-full bg-[#f59e0b] text-[#0a1f55] flex items-center justify-center text-[12px] font-extrabold shrink-0">!</div>
            <p className="text-[13.5px] text-slate-700 leading-relaxed">
              <strong className="text-[#b45309]">Importante:</strong> per iscriversi al corso è necessario essere
              <strong className="text-[#0a1f55]"> tesserati A.C.D.T.</strong> Se non sei già socio, scegli
              il pacchetto <strong>&laquo;Corso + Tessera&raquo;</strong> o <strong>&laquo;Premium&raquo;</strong> per includere automaticamente la tessera.
            </p>
          </div>

          {/* Status socio (visibile solo se NON socio, per ricordare la restrizione) */}
          {authChecked && !isSocio && (
            <div className="mt-3 inline-flex items-center gap-2 text-[12.5px]" data-testid="status-socio">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-600 font-semibold">
                <Lock className="h-3.5 w-3.5" /> Non sei ancora socio — &laquo;Solo Corso&raquo; richiede tesseramento
              </span>
            </div>
          )}

          {/* Redirect notice quando un non-socio prova a cliccare Solo Corso */}
          {redirectNotice && (
            <div className="mt-3 max-w-2xl mx-auto rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-800 text-left" data-testid="redirect-notice">
              <strong>Pacchetto riservato ai soci.</strong> Ti abbiamo selezionato automaticamente <strong>&laquo;Corso + Tessera&raquo;</strong> (€ 149) che include la tessera A.C.D.T. necessaria.
            </div>
          )}
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {COURSE_OPTIONS.map((o) => {
            const selected = o.id === optionId;
            const locked = o.soloSoci && authChecked && !isSocio;
            const prezzoListino = PROMO.active ? prezzoListinoPacchetto(o.price) : null;
            return (
              <button
                key={o.id}
                type="button"
                data-testid={`pacchetto-${o.id}`}
                onClick={() => handleSelectOption(o)}
                className={`reveal in-view text-left relative rounded-2xl border bg-white p-7 lift ${
                  selected
                    ? "border-[#102a6b] shadow-[0_24px_70px_-25px_rgba(16,42,107,0.45)] ring-2 ring-[#102a6b]/15"
                    : locked
                    ? "border-slate-200 opacity-80 hover:opacity-100 hover:border-[#f59e0b]/60"
                    : "border-slate-200 hover:border-[#102a6b]/50"
                }`}
              >
                {o.badge && (
                  <div className={`absolute -top-3 left-6 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                    o.soloSoci ? "bg-slate-800 text-[#fbbf24]" : "bg-[#f59e0b] text-[#0a1f55]"
                  }`}>
                    {o.soloSoci && <Lock className="inline h-3 w-3 mr-1 -mt-0.5" />}
                    {o.badge}
                  </div>
                )}
                {PROMO.active && (
                  <div className="absolute -top-3 right-6 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-red-600 text-white shadow-md flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> -{PROMO.scontoEuro}€
                  </div>
                )}
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-lg font-extrabold text-[#0a1f55] leading-snug">{o.title}</h3>
                  <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                    selected ? "bg-[#102a6b] border-[#102a6b]" : "border-slate-300"
                  }`}>
                    {selected && <Check className="h-3.5 w-3.5 text-white" />}
                  </div>
                </div>
                <div className="mt-4 flex items-baseline gap-2 flex-wrap">
                  {PROMO.active && (
                    <span className="text-lg font-bold text-slate-400 line-through" data-testid={`prezzo-listino-${o.id}`}>
                      {formatPrice(prezzoListino)}
                    </span>
                  )}
                  <span className="text-4xl font-extrabold text-[#102a6b]" data-testid={`prezzo-promo-${o.id}`}>{formatPrice(o.price)}</span>
                  <span className="text-xs text-slate-500">/ una tantum</span>
                </div>
                {PROMO.active && (
                  <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-red-600 uppercase tracking-wider">
                    <Sparkles className="h-3 w-3" /> {PROMO.labelOfferta}
                  </div>
                )}
                <div className="mt-4 space-y-2 border-t border-dashed border-slate-200 pt-4">
                  {o.items.map((it) => (
                    <div key={it.label} className="flex items-start justify-between gap-3 text-[13px]">
                      <span className="text-slate-700">{it.label}</span>
                      <span className="font-semibold text-[#0a1f55] whitespace-nowrap">
                        {it.free ? <span className="text-[#f59e0b]">Incluso</span> : formatPrice(it.price)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className={`mt-5 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                  locked
                    ? "bg-slate-800 text-[#fbbf24]"
                    : "bg-[#102a6b] text-white"
                }`}>
                  {locked ? <><Lock className="h-3.5 w-3.5 mr-1.5" /> Riservato ai soci</> : <><CreditCard className="h-3.5 w-3.5 mr-1.5" /> Scegli e paga</>}
                </div>
              </button>
            );
          })}
        </div>

      </div>
      {checkoutOpt && (
        <CheckoutModal
          pacchetto={{
            id: checkoutOpt.id,
            name: checkoutOpt.title,
            price: checkoutOpt.price,
            items: checkoutOpt.items,
          }}
          onSubmit={handleModalSubmit}
          onClose={() => setCheckoutOpt(null)}
          submitting={submitting}
        />
      )}
      {certModal && (
        <CertificazioniModal
          prezzoBase={option.price}
          packageName={option.title}
          onClose={() => setCertModal(null)}
          onConfirm={async ({ cert_l4, cert_cfu }) => {
            const p = certModal;
            setCertModal(null);
            await doCheckoutCourse({ package_id: p.package_id, billing: p.billing, cert_l4, cert_cfu });
          }}
        />
      )}
    </section>
  );
}

function MethodMark({ id }) {
  if (id === "paypal") return (<span className="text-[12px] font-extrabold"><span className="text-[#003087]">Pay</span><span className="text-[#009cde]">Pal</span></span>);
  if (id === "googlepay") return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border border-slate-300 text-[10px] font-bold">
      <svg className="h-3 w-3" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.97 10.97 0 0 0 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.43.36-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
      </svg>
      <span className="text-slate-700">G Pay</span>
    </span>
  );
  if (id === "applepay") return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black text-white text-[10px] font-bold">
      <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.5 12.5c0-2.5 2-3.7 2.1-3.7-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.8-3.2-.8-1.6 0-3.2 1-4 2.5-1.7 3-.4 7.4 1.2 9.8.8 1.2 1.8 2.5 3.1 2.5 1.2 0 1.7-.8 3.2-.8 1.5 0 1.9.8 3.2.8 1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.8 1.3-2.9-.1 0-2.5-1-2.5-3.9zM14.9 4.7c.7-.8 1.1-2 1-3.2-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3.1 1.1.1 2.2-.6 2.9-1.4z"/>
      </svg>
      <span>Pay</span>
    </span>
  );
  if (id === "klarna") return (<span className="px-1.5 py-0.5 rounded bg-[#FFA8CD] text-[#17120F] text-[10px] font-extrabold">Klarna</span>);
  return <Building2 className="h-4 w-4 text-[#102a6b]" />;
}

function MethodDetails({ method }) {
  if (method === "paypal")
    return (
      <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#003087]/10 text-[#003087] text-[10.5px] font-extrabold uppercase tracking-wider">Rateizzabile</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[10.5px] font-extrabold uppercase tracking-wider">3 · 6 · 12 rate</span>
        </div>
        <p className="text-[13.5px] text-slate-600">Verrai reindirizzato su <strong>PayPal</strong>. Puoi pagare in un&apos;unica soluzione oppure scegliere <strong>PayPal Paga in 3, 6 o 12 rate</strong>.</p>
      </div>
    );
  if (method === "googlepay")
    return (<div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5"><p className="text-[13.5px] text-slate-600">Verrai reindirizzato per autorizzare il pagamento tramite <strong>Google Pay</strong>.</p></div>);
  if (method === "applepay")
    return (<div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5"><p className="text-[13.5px] text-slate-600">Conferma il pagamento con <strong>Apple Pay</strong> tramite Touch ID, Face ID o passcode.</p></div>);
  if (method === "klarna")
    return (
      <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#FFA8CD] text-[#17120F] text-[10.5px] font-extrabold uppercase tracking-wider">Rateizzabile</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[10.5px] font-extrabold uppercase tracking-wider">3 · 6 · 12 rate</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 text-[10.5px] font-extrabold uppercase tracking-wider">Senza interessi</span>
        </div>
        <p className="text-[13.5px] text-slate-600">Con <strong>Klarna</strong> puoi rateizzare in <strong>3, 6 o 12 rate senza interessi</strong>. L&apos;idoneità viene verificata al checkout.</p>
      </div>
    );
  if (method === "bonifico")
    return (
      <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5">
        <div className="flex items-center gap-2 text-[#0a1f55] font-semibold text-sm mb-3"><Building2 className="h-4 w-4" /> Coordinate ACDT</div>
        <div className="grid sm:grid-cols-2 gap-3 text-[13px]">
          <Row k="Intestatario" v="A.C.D.T. Associazione" />
          <Row k="IBAN" v="IT00 X000 0000 0000 0000 0000 000" />
          <Row k="BIC / SWIFT" v="XXXXITXXXXX" />
          <Row k="Causale" v="Corso Consulente Digitale – Nome Cognome" />
        </div>
        <p className="mt-3 text-[12px] text-slate-500">Riceverai per email lo statino di pagamento con causale personalizzata.</p>
      </div>
    );
  return null;
}
function Row({ k, v }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{k}</div>
      <div className="text-[#0a1f55] font-semibold break-all">{v}</div>
    </div>
  );
}
function Field({ id, label, value, onChange, type = "text" }) {
  return (
    <div>
      <Label htmlFor={id} className="text-[13px] font-semibold text-[#0a1f55]">{label}</Label>
      <Input id={id} type={type} value={value} onChange={onChange}
        className="mt-1.5 h-11 border-slate-300 focus-visible:ring-[#102a6b]/40"
        placeholder={label.replace(" *", "")} />
    </div>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section className="relative py-20 md:py-24">
      <div className="max-w-4xl mx-auto px-5 md:px-8">
        <div className="text-center mb-10 reveal">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">FAQ</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a1f55]">Domande frequenti</h2>
        </div>
        <div className="space-y-3">
          {COURSE.faqs.map((f, i) => {
            const active = open === i;
            return (
              <div key={f.q} className="reveal rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <button type="button" onClick={() => setOpen(active ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 md:px-6 py-4 text-left">
                  <span className="text-[15px] font-bold text-[#0a1f55]">{f.q}</span>
                  <ChevronDown className={`h-5 w-5 text-[#102a6b] transition-transform ${active ? "rotate-180" : ""}`} />
                </button>
                <div className={`overflow-hidden transition-[max-height] duration-300 ${active ? "max-h-60" : "max-h-0"}`}>
                  <p className="px-5 md:px-6 pb-5 text-[14px] text-slate-600 leading-relaxed">{f.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
